/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ActionFlow,
  compoundLogicalAction,
  constantNoop,
  createFlow,
  exitAction,
  exitFlow,
  goToStep,
  LogicalAction,
  logicalNoop,
  sideEffectAction,
} from '@gh/common-shared';
import { TranslationTuple } from '@gh/core-mls';
import { isNil } from 'lodash';
import { CommonActions } from '../common-actions.service';
import { FormActionFlows } from './form-action-flows.service';
import { highlightAll, whenFormInvalid } from './form-action.utils';
import { FormActions, FormFlowState } from './form-actions.service';

export interface CompletePipelineConfig<S> {
  noSuccessNotification?: boolean;
  successMessage?: string | TranslationTuple | TransformFn<any, TranslationTuple>;
  validateForm?: LogicalAction<S>;
  validate?: LogicalAction<S>;
  execute: LogicalAction<S>;
  finish?: ProcedureFn<S>;
  error?: ProcedureFn<S>;
}

@Injectable()
export class FormActionPipelines {
  constructor(private flows: FormActionFlows,
              private commonActions: CommonActions,
              private formActions: FormActions) {

  }

  complete<S extends FormFlowState<any>>(pipeline: CompletePipelineConfig<S>): ActionFlow<S> {
    return createFlow({
      'start': goToStep(({ trigger }: S) => {
        if (isNil(trigger)) {
          throw new Error(`Complete flow trigger is undefined`);
        }
        return `start.${trigger}`;
      }),
      'start.process': this.process(pipeline).bindAs('finish'),
      'start.deactivate': this.deactivate(pipeline).bindAs('finish'),
      '*': exitFlow<S>(),
    });
  }

  process<S extends FormFlowState<any>>(pipeline: CompletePipelineConfig<S>): ActionFlow<S> {
    return this.flows.updateFlow(this.updateAction(pipeline), pipeline.finish);
  }

  recalculate<S extends FormFlowState<any>>(pipeline: CompletePipelineConfig<S>): ActionFlow<S> {
    return this.flows.recalculateFlow(this.updateAction({ ...pipeline, noSuccessNotification: true }), pipeline.finish);
  }

  deactivate<S extends FormFlowState<any>>(pipeline: CompletePipelineConfig<S>): ActionFlow<S> {
    return this.flows.deactivateFlow(this.updateAction(pipeline));
  }

  private updateAction<S extends FormFlowState<any>>(pipeline: CompletePipelineConfig<S>): LogicalAction<S> {
    const validateForm = (pipeline.validateForm ? pipeline.validateForm : this.commonActions.validateWithEffect(
      whenFormInvalid,
      highlightAll));
    const validate = pipeline.validate ? pipeline.validate : logicalNoop();
    const success = pipeline.noSuccessNotification ? constantNoop()
      : this.formActions.successNotification(<any>pipeline.successMessage);
    const error = pipeline.error ? sideEffectAction(pipeline.error) : this.commonActions.notifications();

    return compoundLogicalAction('save.success', 'save.fail', {
      'start': validateForm.bindAs('validateForm.success', 'validateForm.fail'),
      'validateForm.success': validate.bindAs('validate.success', 'validate.fail'),
      'validate.success': pipeline.execute.bindAs('execute.success', 'execute.fail'),
      'execute.success': success.bindAs('success'),
      'validate.fail': goToStep('notifications'),
      'execute.fail': goToStep('notifications'),
      'notifications': error.bindAs('fail'),
      'success': exitAction('save.success', <Partial<S>>{ success: true }),
      '*': exitAction('save.fail', <Partial<S>>{ success: false }),
    });
  }
}
